const FREQ_DAILY = "DAILY";
const FREQ_WEEKLY = "WEEKLY";
const FREQ_MONTHLY = "MONTHLY";
const FREQ_YEARLY = "YEARLY";

const MONTHLY_MODE_DAY_OF_MONTH = "dayOfMonth";
const MONTHLY_MODE_NTH_WEEKDAY = "nthWeekday";

const END_MODE_NEVER = "never";
const END_MODE_ON = "on";
const END_MODE_AFTER = "after";

const POSITION_LAST = -1;

const MILLISECONDS_PER_DAY = 86400 * 1000;

const SUPPORTED_FREQUENCIES = [FREQ_DAILY, FREQ_WEEKLY, FREQ_MONTHLY, FREQ_YEARLY];

const SUPPORTED_PARTS = [
  "FREQ",
  "INTERVAL",
  "BYDAY",
  "BYMONTHDAY",
  "BYSETPOS",
  "COUNT",
  "UNTIL",
];

const WEEKDAYS = ["SU", "MO", "TU", "WE", "TH", "FR", "SA"];

const WEEKDAY_NAMES = {
  SU: "Sunday",
  MO: "Monday",
  TU: "Tuesday",
  WE: "Wednesday",
  TH: "Thursday",
  FR: "Friday",
  SA: "Saturday",
};

const WEEKDAYS_MONDAY_TO_FRIDAY = ["MO", "TU", "WE", "TH", "FR"];

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const MONTH_ABBREVIATIONS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const POSITION_WORDS = {
  1: "first",
  2: "second",
  3: "third",
  4: "fourth",
  5: "fifth",
  [POSITION_LAST]: "last",
};

const WEEKS_PER_MONTH_POSITION = 7;
const LAST_POSSIBLE_WEEK_POSITION = 5;
const LAST_DAY_OF_ANY_MONTH = 31;

class RecurrenceRuleError extends Error {
  constructor(message, context = {}) {
    super(`${message} (${JSON.stringify(context)})`);
    this.name = "RecurrenceRuleError";
    this.context = context;
  }
}

function pad(value, width) {
  return String(value).padStart(width, "0");
}

function toUtcStamp(date) {
  return `${date.getUTCFullYear()}${pad(date.getUTCMonth() + 1, 2)}${pad(date.getUTCDate(), 2)}`
    + `T${pad(date.getUTCHours(), 2)}${pad(date.getUTCMinutes(), 2)}${pad(date.getUTCSeconds(), 2)}Z`;
}

function formatUntilUtc(recurringEndTimestamp) {
  const timestamp = Number(recurringEndTimestamp);
  if (!timestamp) {
    return null;
  }

  const chosenDay = new Date(timestamp * 1000);
  const endOfChosenDay = new Date(chosenDay);
  endOfChosenDay.setHours(23, 59, 59, 0);

  if (endOfChosenDay.getTime() < chosenDay.getTime()) {
    endOfChosenDay.setTime(chosenDay.getTime() + MILLISECONDS_PER_DAY - 1000);
  }

  return toUtcStamp(endOfChosenDay);
}

function untilStampToTimestamp(stamp) {
  const match = /^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z$/.exec(stamp);

  if (!match) {
    throw new RecurrenceRuleError("This repeat rule ends on something that is not a date", {
      until: stamp,
    });
  }

  const [, year, month, day, hour, minute, second] = match;

  const instant = new Date(Date.UTC(
    Number(year),
    Number(month) - 1,
    Number(day),
    Number(hour),
    Number(minute),
    Number(second),
  ));

  const localDay = new Date(instant.getTime());
  localDay.setHours(0, 0, 0, 0);

  return Math.floor(localDay.getTime() / 1000);
}

function weekdayOf(timestamp) {
  return WEEKDAYS[new Date(Number(timestamp) * 1000).getDay()];
}

function dayOfMonthOf(timestamp) {
  return new Date(Number(timestamp) * 1000).getDate();
}

function weekPositionOf(timestamp) {
  const position = Math.ceil(dayOfMonthOf(timestamp) / WEEKS_PER_MONTH_POSITION);

  return position >= LAST_POSSIBLE_WEEK_POSITION ? POSITION_LAST : position;
}

function sortWeekdays(days) {
  return [...days].sort((left, right) => WEEKDAYS.indexOf(left) - WEEKDAYS.indexOf(right));
}

function normaliseEnd(end) {
  const requested = end && end.mode ? end.mode : END_MODE_NEVER;

  if (requested === END_MODE_AFTER) {
    return { mode: END_MODE_AFTER, count: Number(end.count) || 0, until: 0 };
  }

  if (requested === END_MODE_ON) {
    return { mode: END_MODE_ON, count: 0, until: Number(end.until) || 0 };
  }

  if (requested !== END_MODE_NEVER) {
    throw new RecurrenceRuleError("This repeat rule ends in a way the editor cannot show", {
      end_mode: requested,
    });
  }

  return { mode: END_MODE_NEVER, count: 0, until: 0 };
}

function assertFrequency(freq) {
  if (!SUPPORTED_FREQUENCIES.includes(freq)) {
    throw new RecurrenceRuleError("This event repeats on a frequency the editor cannot show", {
      freq,
      supported: SUPPORTED_FREQUENCIES,
    });
  }
}

function assertInterval(interval) {
  if (!Number.isInteger(interval) || interval < 1) {
    throw new RecurrenceRuleError("A repeating event has to repeat at least every one period", {
      interval,
    });
  }
}

function assertWeekdays(byDay) {
  if (!Array.isArray(byDay) || byDay.length === 0) {
    throw new RecurrenceRuleError("A weekly event has to repeat on at least one day", {
      by_day: byDay,
    });
  }

  byDay.forEach(day => {
    if (!WEEKDAYS.includes(day)) {
      throw new RecurrenceRuleError("That is not a day of the week", {
        day,
        supported: WEEKDAYS,
      });
    }
  });
}

function assertEnd(end) {
  if (end.mode === END_MODE_AFTER && (!Number.isInteger(end.count) || end.count < 1)) {
    throw new RecurrenceRuleError("A series that ends after a number of times needs that number", {
      count: end.count,
    });
  }

  if (end.mode === END_MODE_ON && !end.until) {
    throw new RecurrenceRuleError("A series that ends on a date needs that date", {
      until: end.until,
    });
  }
}

function assertWeekPosition(position) {
  if (!(position in POSITION_WORDS)) {
    throw new RecurrenceRuleError("A monthly event repeats on the first to fifth or last weekday", {
      position,
    });
  }
}

function assertMonthDay(monthDay) {
  if (!Number.isInteger(monthDay) || monthDay < 1 || monthDay > LAST_DAY_OF_ANY_MONTH) {
    throw new RecurrenceRuleError("That is not a day of the month", {
      month_day: monthDay,
    });
  }
}

function monthlyParts(rule) {
  if (rule.monthlyMode === MONTHLY_MODE_NTH_WEEKDAY) {
    const nthWeekday = rule.nthWeekday || {};

    if (!WEEKDAYS.includes(nthWeekday.day)) {
      throw new RecurrenceRuleError("That is not a day of the week", {
        day: nthWeekday.day,
        supported: WEEKDAYS,
      });
    }

    assertWeekPosition(nthWeekday.position);

    return [`BYDAY=${nthWeekday.day}`, `BYSETPOS=${nthWeekday.position}`];
  }

  if (rule.monthlyMode === MONTHLY_MODE_DAY_OF_MONTH) {
    const monthDay = Number(rule.monthDay);

    assertMonthDay(monthDay);

    return [`BYMONTHDAY=${monthDay}`];
  }

  throw new RecurrenceRuleError("A monthly event repeats either on a date or on a weekday", {
    monthly_mode: rule.monthlyMode,
  });
}

function buildRRule(rule) {
  if (!rule) {
    return "";
  }

  const freq = String(rule.freq || "").toUpperCase();
  assertFrequency(freq);

  const interval = Number(rule.interval);
  assertInterval(interval);

  const parts = [`FREQ=${freq}`];

  if (interval > 1) {
    parts.push(`INTERVAL=${interval}`);
  }

  if (freq === FREQ_WEEKLY) {
    assertWeekdays(rule.byDay);
    parts.push(`BYDAY=${sortWeekdays(rule.byDay).join(",")}`);
  }

  if (freq === FREQ_MONTHLY) {
    monthlyParts(rule).forEach(part => parts.push(part));
  }

  const end = normaliseEnd(rule.end);
  assertEnd(end);

  if (end.mode === END_MODE_AFTER) {
    parts.push(`COUNT=${end.count}`);
  }

  if (end.mode === END_MODE_ON) {
    parts.push(`UNTIL=${formatUntilUtc(end.until)}`);
  }

  return parts.join(";");
}

function readParts(rruleString) {
  const trimmed = String(rruleString || "").trim().toUpperCase();

  if (trimmed === "") {
    throw new RecurrenceRuleError("This event says it repeats but carries no repeat rule", {
      rrule: rruleString,
    });
  }

  const parts = {};

  trimmed.split(";").forEach(segment => {
    const separatorIndex = segment.indexOf("=");

    if (separatorIndex < 1) {
      throw new RecurrenceRuleError("This repeat rule is not readable", {
        rrule: rruleString,
        segment,
      });
    }

    const key = segment.substring(0, separatorIndex);

    if (!SUPPORTED_PARTS.includes(key)) {
      throw new RecurrenceRuleError("This repeat rule uses something the editor cannot show", {
        rrule: rruleString,
        part: key,
        supported: SUPPORTED_PARTS,
      });
    }

    parts[key] = segment.substring(separatorIndex + 1);
  });

  return parts;
}

function readEnd(parts) {
  if (parts.COUNT !== undefined && parts.UNTIL !== undefined) {
    throw new RecurrenceRuleError("This repeat rule both stops on a date and after a count", {
      count: parts.COUNT,
      until: parts.UNTIL,
    });
  }

  if (parts.COUNT !== undefined) {
    return { mode: END_MODE_AFTER, count: Number(parts.COUNT), until: 0 };
  }

  if (parts.UNTIL !== undefined) {
    return { mode: END_MODE_ON, count: 0, until: untilStampToTimestamp(parts.UNTIL) };
  }

  return { mode: END_MODE_NEVER, count: 0, until: 0 };
}

function readWeekdayList(byDay) {
  return byDay.split(",").map(entry => {
    const day = entry.replace(/^[+-]?\d+/, "");

    if (!WEEKDAYS.includes(day)) {
      throw new RecurrenceRuleError("That is not a day of the week", {
        day: entry,
        supported: WEEKDAYS,
      });
    }

    return day;
  });
}

function readWeekdayPosition(byDay) {
  const prefix = /^([+-]?\d+)/.exec(byDay);

  return prefix ? Number(prefix[1]) : null;
}

function parseRRule(rruleString, anchorTimestamp = 0) {
  const parts = readParts(rruleString);

  const freq = parts.FREQ;

  if (freq === undefined) {
    throw new RecurrenceRuleError("This repeat rule never says how often it repeats", {
      rrule: rruleString,
    });
  }

  assertFrequency(freq);

  const rule = {
    freq,
    interval: parts.INTERVAL === undefined ? 1 : Number(parts.INTERVAL),
    byDay: [],
    monthlyMode: null,
    monthDay: 0,
    nthWeekday: null,
    end: readEnd(parts),
  };

  assertInterval(rule.interval);
  assertEnd(rule.end);

  if (freq === FREQ_WEEKLY) {
    rule.byDay = parts.BYDAY === undefined
      ? [weekdayOf(anchorTimestamp)].filter(Boolean)
      : sortWeekdays(readWeekdayList(parts.BYDAY));
  }

  if (freq === FREQ_MONTHLY) {
    const position = parts.BYSETPOS === undefined
      ? parts.BYDAY === undefined ? null : readWeekdayPosition(parts.BYDAY)
      : Number(parts.BYSETPOS);

    if (parts.BYDAY !== undefined && position !== null) {
      rule.monthlyMode = MONTHLY_MODE_NTH_WEEKDAY;
      rule.nthWeekday = { position, day: readWeekdayList(parts.BYDAY)[0] };
      assertWeekPosition(position);
    } else if (parts.BYMONTHDAY !== undefined) {
      rule.monthlyMode = MONTHLY_MODE_DAY_OF_MONTH;
      rule.monthDay = Number(parts.BYMONTHDAY);
      assertMonthDay(rule.monthDay);
    } else if (anchorTimestamp) {
      rule.monthlyMode = MONTHLY_MODE_DAY_OF_MONTH;
      rule.monthDay = dayOfMonthOf(anchorTimestamp);
    }
  }

  return rule;
}

function toRule(ruleOrString, anchorTimestamp) {
  return typeof ruleOrString === "string"
    ? parseRRule(ruleOrString, anchorTimestamp)
    : ruleOrString;
}

function joinWithAnd(names) {
  if (names.length === 1) {
    return names[0];
  }

  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

function isMondayToFriday(byDay) {
  return byDay.length === WEEKDAYS_MONDAY_TO_FRIDAY.length
    && WEEKDAYS_MONDAY_TO_FRIDAY.every(day => byDay.includes(day));
}

function describeEnd(end) {
  if (end.mode === END_MODE_AFTER) {
    return `, ${end.count} times`;
  }

  if (end.mode === END_MODE_ON) {
    const day = new Date(end.until * 1000);

    return `, until ${day.getDate()} ${MONTH_ABBREVIATIONS[day.getMonth()]} ${day.getFullYear()}`;
  }

  return "";
}

function describeFrequency(rule, anchorTimestamp) {
  const { freq, interval } = rule;

  if (freq === FREQ_DAILY) {
    return interval === 1 ? "Daily" : `Every ${interval} days`;
  }

  if (freq === FREQ_WEEKLY) {
    if (interval === 1 && isMondayToFriday(rule.byDay)) {
      return "Every weekday (Monday to Friday)";
    }

    const dayNames = joinWithAnd(sortWeekdays(rule.byDay).map(day => WEEKDAY_NAMES[day]));

    return interval === 1
      ? `Weekly on ${dayNames}`
      : `Every ${interval} weeks on ${dayNames}`;
  }

  if (freq === FREQ_MONTHLY) {
    const lead = interval === 1 ? "Monthly on " : `Every ${interval} months on `;

    if (rule.monthlyMode === MONTHLY_MODE_NTH_WEEKDAY) {
      const { position, day } = rule.nthWeekday;

      return `${lead}the ${POSITION_WORDS[position]} ${WEEKDAY_NAMES[day]}`;
    }

    return `${lead}day ${rule.monthDay || dayOfMonthOf(anchorTimestamp)}`;
  }

  const anchor = new Date(Number(anchorTimestamp) * 1000);
  const dayInYear = `${MONTH_NAMES[anchor.getMonth()]} ${anchor.getDate()}`;

  return interval === 1
    ? `Annually on ${dayInYear}`
    : `Every ${interval} years on ${dayInYear}`;
}

function describeRule(ruleOrString, anchorTimestamp = 0) {
  const rule = toRule(ruleOrString, anchorTimestamp);

  if (!rule) {
    return "";
  }

  return `${describeFrequency(rule, anchorTimestamp)}${describeEnd(normaliseEnd(rule.end))}`;
}

function ruleFromDate(anchorTimestamp) {
  return {
    freq: FREQ_WEEKLY,
    interval: 1,
    byDay: [weekdayOf(anchorTimestamp)],
    monthlyMode: null,
    monthDay: 0,
    nthWeekday: null,
    end: { mode: END_MODE_NEVER, count: 0, until: 0 },
  };
}

function presetsForDate(anchorTimestamp) {
  const weekday = weekdayOf(anchorTimestamp);

  const rules = [
    { key: "none", rule: null },
    {
      key: "daily",
      rule: {
        freq: FREQ_DAILY,
        interval: 1,
        byDay: [],
        monthlyMode: null,
        monthDay: 0,
        nthWeekday: null,
        end: { mode: END_MODE_NEVER, count: 0, until: 0 },
      },
    },
    { key: "weekly", rule: ruleFromDate(anchorTimestamp) },
    {
      key: "monthly",
      rule: {
        freq: FREQ_MONTHLY,
        interval: 1,
        byDay: [],
        monthlyMode: MONTHLY_MODE_NTH_WEEKDAY,
        monthDay: 0,
        nthWeekday: { position: weekPositionOf(anchorTimestamp), day: weekday },
        end: { mode: END_MODE_NEVER, count: 0, until: 0 },
      },
    },
    {
      key: "yearly",
      rule: {
        freq: FREQ_YEARLY,
        interval: 1,
        byDay: [],
        monthlyMode: null,
        monthDay: 0,
        nthWeekday: null,
        end: { mode: END_MODE_NEVER, count: 0, until: 0 },
      },
    },
    {
      key: "weekdays",
      rule: {
        freq: FREQ_WEEKLY,
        interval: 1,
        byDay: [...WEEKDAYS_MONDAY_TO_FRIDAY],
        monthlyMode: null,
        monthDay: 0,
        nthWeekday: null,
        end: { mode: END_MODE_NEVER, count: 0, until: 0 },
      },
    },
  ];

  return rules.map(preset => ({
    key: preset.key,
    rule: preset.rule,
    label: preset.rule === null
      ? "Does not repeat"
      : describeRule(preset.rule, anchorTimestamp),
  }));
}

function rulesMatch(left, right) {
  if (!left || !right) {
    return left === right;
  }

  try {
    return buildRRule(left) === buildRRule(right);
  } catch (error) {
    return false;
  }
}

function ruleToSave(storedRrule, rule, anchorTimestamp) {
  const built = buildRRule(rule);
  const stored = typeof storedRrule === "string" ? storedRrule.trim() : "";

  if (stored === "") {
    return built;
  }

  try {
    return buildRRule(parseRRule(stored, anchorTimestamp)) === built ? stored : built;
  } catch (error) {
    return built;
  }
}

module.exports = {
  ruleToSave,
  FREQ_DAILY,
  FREQ_WEEKLY,
  FREQ_MONTHLY,
  FREQ_YEARLY,
  MONTHLY_MODE_DAY_OF_MONTH,
  MONTHLY_MODE_NTH_WEEKDAY,
  END_MODE_NEVER,
  END_MODE_ON,
  END_MODE_AFTER,
  POSITION_LAST,
  WEEKDAYS,
  WEEKDAY_NAMES,
  RecurrenceRuleError,
  buildRRule,
  parseRRule,
  describeRule,
  presetsForDate,
  ruleFromDate,
  rulesMatch,
  formatUntilUtc,
};
